import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import Image1 from "../Assets/Clentaine/WEB IMG (1).png";
import Image2 from "../Assets/Clentaine/12.png";

const projectsData = [
  {
    id: "celentaine",
    title: "Celentaine",
    description:
      "Experience the epitome of modern architectural elegance. Celentaine blends seamless indoor-outdoor living with sophisticated design elements.",
    image: Image1,
    homeLink: "/celentaine",
    magazineLink: "/Celandine-The-Sunlit-Bloom",
  },
  {
    id: "lakshmi-praba",
    title: "Lakshmi Praba",
    description:
      "A testament to timeless design and structural excellence. Discover the perfect blend of comfort, style, and innovation in every corner.",
    image: Image2,
    homeLink: "/lakshmi-praba",
    magazineLink: "/lakshmi-praba-magazine",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const ProjectSection = ({ isDarkMode }) => {
  const navigate = useNavigate();

  return (
    <section
      className={`w-full py-24 px-6 md:px-16 lg:px-28 transition-colors duration-500 ${isDarkMode ? "bg-[#0d0d0d] text-white" : "bg-[#F5F5F5] text-black"
        }`}
    >
      {/* Section Heading */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-16 text-center"
      >
        <p
          className={`text-xs tracking-[0.3em] uppercase font-semibold mb-3 ${isDarkMode ? "text-[#FEF200]" : "text-gray-400"
            }`}
        >
          Our Portfolio
        </p>
        <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
          Featured Projects
        </h2>
        <div
          className={`mx-auto mt-4 h-[2px] w-14 rounded-full ${isDarkMode ? "bg-[#FEF200]" : "bg-black"
            }`}
        />
      </motion.div>

      {/* ── 2 Cards Side by Side in one row ── */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-8"
      >
        {projectsData.map((project, index) => (
          <motion.div
            key={project.id}
            variants={cardVariants}
            className={`group flex flex-col rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow duration-500 ${isDarkMode ? "bg-[#1a1a1a]" : "bg-white"
              }`}
          >
            {/* Project Image — clickable, goes to home page */}
            <div
              className="relative w-full h-72 overflow-hidden cursor-pointer"
              onClick={() => project.homeLink && navigate(project.homeLink)}
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />

              {/* Index badge */}
              <div
                className={`absolute top-4 left-4 text-xs font-bold tracking-widest px-3 py-1 rounded-full ${isDarkMode
                  ? "bg-[#FEF200] text-black"
                  : "bg-black text-white"
                  }`}
              >
                {String(index + 1).padStart(2, "0")}
              </div>

              {/* Coming Soon overlay if no link */}
              {!project.homeLink && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="text-white text-sm font-semibold tracking-widest uppercase">
                    Coming Soon
                  </span>
                </div>
              )}
            </div>

            {/* Card Content */}
            <div className="flex flex-col flex-1 p-7 gap-4">
              <h3 className="text-2xl font-bold tracking-tight">
                {project.title}
              </h3>
              <p
                className={`text-sm leading-relaxed flex-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
              >
                {project.description}
              </p>

              {/* Divider */}
              <div
                className={`w-full h-px ${isDarkMode ? "bg-gray-800" : "bg-gray-100"
                  }`}
              />

              {/* Buttons */}
              <div className="flex flex-wrap gap-3">
                {project.homeLink && (
                  <button
                    onClick={() => navigate(project.homeLink)}
                    className={`flex-1 py-3 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${isDarkMode
                      ? "bg-[#FEF200] text-black hover:bg-yellow-300"
                      : "bg-black text-white hover:bg-gray-800"
                      }`}
                  >
                    View Home
                  </button>
                )}
                {project.magazineLink && (
                  <button
                    onClick={() => navigate(project.magazineLink)}
                    className={`flex-1 py-3 rounded-full font-semibold text-sm tracking-wide border transition-all duration-300 hover:-translate-y-0.5 ${isDarkMode
                      ? "border-gray-600 text-white hover:border-[#FEF200] hover:text-[#FEF200]"
                      : "border-black text-black hover:bg-black hover:text-white"
                      }`}
                  >
                    E-Magazine
                  </button>
                )}
                {!project.homeLink && !project.magazineLink && (
                  <span
                    className={`flex-1 py-3 text-center rounded-full text-sm font-semibold border ${isDarkMode
                      ? "border-gray-700 text-gray-600"
                      : "border-gray-200 text-gray-400"
                      }`}
                  >
                    Coming Soon
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default ProjectSection;
